import { envConfig } from '#constants/envConfig';
import { probeServedBuild, type ServedBuild } from './buildCheck';
import { isReloadBlocked } from './reloadGuards';

export const RELOAD_KEY = 'chunk-error:reloaded-at';

/** Держим экран обновления минимум столько: иначе перезагрузка мигает и читается как глюк. */
export const MIN_UPDATE_SCREEN_MS = 700;

/**
 * Сколько ждать, прежде чем разрешить ещё одну автоперезагрузку в этой вкладке.
 *
 * Минуты хватает: настоящий выкат случается раз в дни, а частая повторная попытка — всегда
 * признак того, что дело не в релизе и перезагрузка не поможет.
 */
export const RELOAD_COOLDOWN_MS = 60_000;

type Storage = Pick<globalThis.Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** Чем кончилась просьба перезагрузиться. Уезжает в телеметрию — см. `report`. */
export type ReloadOutcome = 'scheduled' | 'cooldown' | 'blocked' | 'native' | ServedBuild;

/**
 * Можно ли перезагрузиться прямо сейчас: либо в этой вкладке автоперезагрузок ещё не было,
 * либо прошлая была достаточно давно.
 *
 * Читает, не трогая: то же значение нужно и на рендере, и в эффекте.
 */
export const canAutoReload = (storage: Storage, now = Date.now()) => {
  try {
    const raw = storage.getItem(RELOAD_KEY);

    if (!raw) {
      return true;
    }

    const last = Number(raw);

    // Значение из старой версии (там лежало 'true') не парсится — считаем, что попытка была
    // давно, и даём ровно одну новую. Иначе вкладки, застрявшие на прошлой сборке, никогда
    // бы не обновились.
    if (!Number.isFinite(last)) {
      return true;
    }

    return now - last > RELOAD_COOLDOWN_MS;
  } catch {
    // Хранилище недоступно (приватный режим, запрет данных сайта). Раньше это была дыра:
    // защёлки нет — значит петля. Теперь петлю закрывает не она, а проверка сборки в
    // requestChunkReload: после успешной перезагрузки сервер отдаёт ту же сборку, что у нас,
    // и второй попытки не будет.
    return true;
  }
};

type RequestInput = {
  storage: Storage;
  reload: () => void;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  probeBuild?: () => Promise<ServedBuild>;
  isBlocked?: () => boolean;
  isNative?: boolean;
  report?: (outcome: ReloadOutcome) => void;
};

/**
 * Решение на вкладку, а не на вызов.
 *
 * Каналов, приносящих одну и ту же поломку, три (`unhandledrejection`, `onCaughtError`,
 * граница ошибок), плюс StrictMode прогоняет эффект дважды. Все они должны получить ОДИН ответ и
 * не устраивать ни второго таймера, ни второго запроса к серверу. Отсюда общий промис на модуль:
 * первый вызов принимает решение, остальные его читают.
 *
 * Заодно это снимает старый баг с вечным спиннером: раньше повторный прогон эффекта видел
 * отметку, поставленную первым прогоном, считал перезагрузку состоявшейся и таймер больше не
 * заводил — экран обновления висел навсегда.
 */
let decision: Promise<boolean> | undefined;

/** Только для тестов: решение живёт в модуле и переживает отдельные кейсы. */
export const resetChunkReloadDecision = () => {
  decision = undefined;
};

const decide = async ({
  storage,
  reload,
  now = Date.now,
  setTimer = setTimeout,
  probeBuild = probeServedBuild,
  isBlocked = isReloadBlocked,
  isNative = envConfig.IS_NATIVE,
  report,
}: RequestInput): Promise<boolean> => {
  const finish = (outcome: ReloadOutcome) => {
    report?.(outcome);

    return outcome === 'scheduled';
  };

  // В приложении чанки лежат в бандле, «пропасть при выкате» им негде, зато reload() там значит
  // холодный старт вебвью — с потерей авторизации до повторной проверки. См. нативную обёртку.
  if (isNative) {
    return finish('native');
  }

  if (isBlocked()) {
    return finish('blocked');
  }

  const startedAt = now();

  if (!canAutoReload(storage, startedAt)) {
    return finish('cooldown');
  }

  // Отметку ставим ДО сетевой проверки: иначе параллельные каналы успеют войти сюда все разом
  // и устроят шторм запросов к серверу.
  try {
    storage.setItem(RELOAD_KEY, String(startedAt));
  } catch {
    // Хранилище недоступно — петлю закрывает проверка сборки ниже.
  }

  const verdict = await probeBuild();

  if (verdict !== 'new-build') {
    return finish(verdict);
  }

  // Браузер показывает старую страницу до первой отрисовки новой, так что на быстром канале
  // перезагрузка успевает произойти за пару кадров — и подсказка мигает, читаясь как глюк.
  // Ждём остаток от MIN_UPDATE_SCREEN_MS: проверка сборки уже съела часть этого времени.
  setTimer(reload, Math.max(0, MIN_UPDATE_SCREEN_MS - (now() - startedAt)));

  return finish('scheduled');
};

/**
 * Просит перезагрузить страницу из-за протухшей сборки. Возвращает `true`, если перезагрузка
 * назначена, и `false`, если её не будет — тогда вызывающему полагается показать обычную ошибку.
 */
export const requestChunkReload = (input: RequestInput): Promise<boolean> => {
  decision ??= decide(input);

  return decision;
};
